/**
 * Screenshot Capture Component
 *
 * Provides UI for:
 * - Capturing screenshot using html2canvas
 * - Pasting image from clipboard
 * - Previewing captured screenshot
 * - Removing screenshot
 */

import { useEffect } from 'react'
import { Camera, Loader2, X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'

interface ScreenshotCaptureProps {
  screenshot: string | null
  isCapturing: boolean
  onCapture: () => Promise<void>
  onPaste: (e: ClipboardEvent) => void
  onClear: () => void
}

export function ScreenshotCapture({
  screenshot,
  isCapturing,
  onCapture,
  onPaste,
  onClear,
}: ScreenshotCaptureProps) {
  // Add paste event listener
  useEffect(() => {
    const pasteArea = document.getElementById('paste-area')
    if (!pasteArea) return

    const handlePaste = (e: Event) => {
      onPaste(e as ClipboardEvent)
    }

    pasteArea.addEventListener('paste', handlePaste)
    return () => pasteArea.removeEventListener('paste', handlePaste)
  }, [onPaste])

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={onCapture}
          disabled={isCapturing}
        >
          {isCapturing ? (
            <>
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              Capturing...
            </>
          ) : (
            <>
              <Camera className="h-4 w-4 mr-2" />
              Capture Screen
            </>
          )}
        </Button>
      </div>

      {!screenshot && (
        <div
          id="paste-area"
          tabIndex={0}
          className={cn(
            'border-2 border-dashed rounded-lg p-8',
            'flex flex-col items-center justify-center',
            'text-muted-foreground text-sm',
            'hover:border-primary/50 transition-colors',
            'focus:outline-none focus:ring-2 focus:ring-primary/20',
            'cursor-pointer'
          )}
        >
          <p className="font-medium mb-1">Paste Screenshot Here</p>
          <p className="text-xs">Click here and press Ctrl+V (Cmd+V on Mac)</p>
        </div>
      )}

      {screenshot && (
        <div className="relative border rounded-lg overflow-hidden bg-muted">
          <img
            src={screenshot}
            alt="Screenshot preview"
            className="w-full h-auto max-h-64 object-contain"
          />
          <Button
            type="button"
            variant="destructive"
            size="icon"
            className="absolute top-2 right-2 h-8 w-8"
            onClick={onClear}
          >
            <X className="h-4 w-4" />
            <span className="sr-only">Remove screenshot</span>
          </Button>
          <div className="absolute bottom-2 left-2 bg-black/70 text-white px-2 py-1 rounded text-xs">
            Screenshot attached
          </div>
        </div>
      )}
    </div>
  )
}
